/**
 * Tenant Smart Devices card — renders the devices assigned to the logged-in
 * tenant and lets them trigger renter-friendly actions (unlock, set temp,
 * toggle light, mute leak alarm, etc.). Uses the same Authorization header
 * pattern the rest of `api` clients use, so the backend `/tenant/devices`
 * endpoint resolves identity via core.me_from_auth.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  Lock, LockOpen, Lightbulb, Plug, Thermometer, Droplets, Camera, Power,
  Activity, ShieldCheck, Cpu, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_ICONS = {
  smart_lock: Lock, internal_smart_lock: Lock, button_pusher: Power,
  video_doorbell: Camera, security_camera: Camera,
  motion_sensor: Activity, contact_sensor: ShieldCheck,
  leak_sensor: Droplets, smart_thermostat: Thermometer,
  smart_light: Lightbulb, smart_plug: Plug,
};

export default function TenantSmartDevices() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setBusy(true);
    try { const { data } = await api.get("/tenant/devices"); setItems(data.items || []); }
    catch (e) { setItems([]); }
    finally { setBusy(false); setInitialLoadDone(true); }
  };
  useEffect(() => { load(); }, []);

  // Only poll if the tenant actually HAS devices — silent poll avoids
  // flipping the UI back to "Loading…" every 8 seconds.
  useEffect(() => {
    if (!initialLoadDone || items.length === 0) return undefined;
    const t = setInterval(() => load({ silent: true }), 12000);
    return () => clearInterval(t);
  }, [initialLoadDone, items.length]);

  const act = async (id, action, payload) => {
    try {
      const { data } = await api.post("/tenant/devices/action", { device_id: id, action, payload });
      toast.success(`${action} → ${data.event_type}`);
      await load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  // Once the initial load completes with zero devices, hide the card so
  // tenants without any IoT in their unit never see a "Loading…" tile.
  if (initialLoadDone && items.length === 0) return null;
  return (
    <Card className="p-5" data-testid="tenant-smart-devices">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Cpu className="w-4 h-4 text-violet-700"/> Smart devices in your unit</h2>
        <button onClick={() => load()} disabled={busy} className="text-xs px-2.5 py-1 rounded-lg border border-slate-300 hover:border-violet-500 inline-flex items-center gap-1" data-testid="tenant-devices-refresh">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`}/> Refresh
        </button>
      </div>
      {busy && items.length === 0 ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((d) => <TenantDeviceTile key={d.id} d={d} onAct={act}/>)}
        </div>
      )}
    </Card>
  );
}

function TenantDeviceTile({ d, onAct }) {
  const Icon = KIND_ICONS[d.kind] || Cpu;
  const s = d.state || {};
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`tenant-device-${d.id}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-5 h-5 text-violet-700 flex-none mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
          <div className="text-[11px] text-slate-500 truncate">{d.location_hint || d.kind.replace(/_/g, " ")}</div>
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
        {(d.kind === "smart_lock" || d.kind === "internal_smart_lock") && (
          <>
            <SmallBtn onClick={() => onAct(d.id, "unlock")} testId={`tenant-unlock-${d.id}`}><LockOpen className="w-3 h-3"/> Unlock</SmallBtn>
            <SmallBtn onClick={() => onAct(d.id, "lock")} testId={`tenant-lock-${d.id}`}><Lock className="w-3 h-3"/> Lock</SmallBtn>
          </>
        )}
        {d.kind === "button_pusher" && <SmallBtn onClick={() => onAct(d.id, "press")} testId={`tenant-press-${d.id}`}>Press</SmallBtn>}
        {d.kind === "smart_light" && (
          <>
            <SmallBtn onClick={() => onAct(d.id, "toggle")} testId={`tenant-toggle-${d.id}`}>{s.on ? "Off" : "On"}</SmallBtn>
            <input type="range" min="0" max="100" value={s.brightness ?? 100}
              onChange={(e) => onAct(d.id, "set_brightness", { brightness: Number(e.target.value) })}
              className="accent-yellow-500 w-24" data-testid={`tenant-bright-${d.id}`}/>
          </>
        )}
        {d.kind === "smart_plug" && <SmallBtn onClick={() => onAct(d.id, "toggle")} testId={`tenant-toggle-${d.id}`}>{s.on ? "Off" : "On"}</SmallBtn>}
        {d.kind === "smart_thermostat" && (
          <>
            <SmallBtn onClick={() => onAct(d.id, "set_temp", { temp_c: Math.max(5, (s.temp_c ?? 21) - 1) })} testId={`tenant-tempdown-${d.id}`}>−1°</SmallBtn>
            <span className="text-xs font-bold tabular-nums">{s.temp_c ?? 21}°C</span>
            <SmallBtn onClick={() => onAct(d.id, "set_temp", { temp_c: Math.min(35, (s.temp_c ?? 21) + 1) })} testId={`tenant-tempup-${d.id}`}>+1°</SmallBtn>
          </>
        )}
        {d.kind === "leak_sensor" && <SmallBtn onClick={() => onAct(d.id, "mute")} testId={`tenant-mute-${d.id}`}>Mute</SmallBtn>}
        {(d.kind === "motion_sensor" || d.kind === "contact_sensor") && (
          <SmallBtn onClick={() => onAct(d.id, s.armed === false ? "arm" : "disarm")} testId={`tenant-armtoggle-${d.id}`}>{s.armed === false ? "Arm" : "Disarm"}</SmallBtn>
        )}
        {(d.kind === "video_doorbell" || d.kind === "security_camera") && (
          <SmallBtn onClick={() => onAct(d.id, "snapshot")} testId={`tenant-snap-${d.id}`}>Snapshot</SmallBtn>
        )}
      </div>
    </div>
  );
}

function SmallBtn({ children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="text-xs px-2 py-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white font-semibold inline-flex items-center gap-1">
      {children}
    </button>
  );
}
